import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { opfsBlobCache } from '@affine/core/utils/opfs-blob-cache';
import type { Workspace as WorkspaceInterface } from '@blocksuite/affine/store';
import { Entity, LiveData, yjsGetPath } from '@toeverything/infra';
import type { Observable } from 'rxjs';
import { Doc as YDoc, transact } from 'yjs';

import { DocsService } from '../../doc';
import { WorkspaceImpl } from '../impls/workspace';
import type { WorkspaceScope } from '../scopes/workspace';
import { WorkspaceEngineService } from '../services/engine';
import {
  resolveWorkspaceBlobFromCacheOrRemote,
  resolveWorkspaceBlobMetadata,
  workspaceBlobCacheToken,
  WorkspaceBlobLocalStorageUnavailableError,
} from '../utils/blob-cache';
import { isDatabaseSystemCollectionDocId } from '../utils/database-row-shard-docs';

export class Workspace extends Entity {
  constructor(
    public readonly scope: WorkspaceScope,
    public readonly featureFlagService: FeatureFlagService
  ) {
    super();
  }

  readonly id = this.scope.props.openOptions.metadata.id;

  readonly openOptions = this.scope.props.openOptions;

  readonly meta = this.scope.props.openOptions.metadata;

  readonly flavour = this.meta.flavour;

  readonly rootYDoc = new YDoc({ guid: this.openOptions.metadata.id });

  _docCollection: WorkspaceInterface | null = null;

  get docCollection() {
    if (!this._docCollection) {
      const workspaceId = this.openOptions.metadata.id;
      const isRestrictedCloudMetadataDoc = (docId: string) =>
        this.flavour === 'affine-cloud' &&
        (docId === workspaceId ||
          docId === `db$${workspaceId}$folders` ||
          docId === `db$${workspaceId}$docProperties`);

      this._docCollection = new WorkspaceImpl({
        id: workspaceId,
        rootDoc: this.rootYDoc,
        featureFlagService: this.featureFlagService,
        blobSource: {
          get: async key => {
            const workspaceId = this.openOptions.metadata.id;
            return await resolveWorkspaceBlobFromCacheOrRemote({
              workspaceId,
              key,
            });
          },
          getMetadata: async key => {
            return resolveWorkspaceBlobMetadata({ workspaceId, key });
          },
          delete: async () => {
            return;
          },
          list: async () => {
            return [];
          },
          set: async (id, blob) => {
            const workspaceId = this.openOptions.metadata.id;
            const cached = await opfsBlobCache.set(workspaceId, id, blob, {
              mime: blob.type,
              size: blob.size,
              source: 'upload',
              cacheToken: workspaceBlobCacheToken(id, blob.size),
            });
            if (!cached) {
              throw new WorkspaceBlobLocalStorageUnavailableError();
            }
            const record = {
              key: id,
              source: blob,
              mime: blob.type,
              size: blob.size,
            };
            await this.engine.blob.set(record);
            // Image/doc CRDT updates may reference this blob immediately after
            // set() resolves. Wait for authoritative cloud upload so other
            // clients do not render permanent "Image not found" placeholders.
            await this.engine.blob.upload(record);
            return id;
          },
          /* eslint-disable rxjs/finnish */
          blobState$: key => this.engine.blob.blobState$(key),
          upload: async key => {
            const cached = await opfsBlobCache.getCompleteFile(
              this.openOptions.metadata.id,
              key
            );
            if (!cached) {
              return this.engine.blob.upload(key);
            }
            return this.engine.blob.upload({
              key,
              source: cached,
              mime: cached.type || 'application/octet-stream',
              size: cached.size,
            });
          },
          name: 'blob',
          readonly: false,
        },
        onLoadDoc: doc => {
          if (isRestrictedCloudMetadataDoc(doc.guid)) {
            return;
          }
          this.engine.doc.connectDoc(doc);
          this.engine.doc.addDoc(doc.guid, 'workspace-doc-load');
        },
        onLoadAwareness: awareness =>
          this.engine.awareness.connectAwareness(awareness),
        shouldLoadAwareness: docId =>
          !isRestrictedCloudMetadataDoc(docId) &&
          !isDatabaseSystemCollectionDocId(docId, this.id),
        onCreateDoc: docId =>
          this.docs.createDoc({ id: docId, skipInit: true }).id,
      });

      // SAFETY: Do NOT auto-delete docs from storage on docMetaRemoved.
      //
      // docMetaRemoved fires whenever _handleDocMetaEvent() detects that a doc ID
      // is no longer in meta.pages. This happens during normal Y.js CRDT sync —
      // e.g. when a remote update temporarily makes pages unreadable or when
      // this._proxy.pages returns undefined (→ docMetas = []) during a merge.
      //
      // Calling deleteDoc() here irreversibly destroys doc data in IDB AND the
      // backend. A single false-positive docMetaRemoved event during sync deletes
      // all user documents permanently. This has caused repeated data loss.
      //
      // Doc storage cleanup must happen through explicit user actions only
      // (e.g. permanent delete from trash), never through CRDT sync observers.
      this._docCollection.meta.docMetaRemoved.subscribe(docId => {
        console.warn(
          `[Workspace] docMetaRemoved fired for ${docId} — storage NOT deleted (safety guard)`
        );
      });
    }
    return this._docCollection;
  }

  get docs() {
    return this.scope.get(DocsService);
  }

  get canGracefulStop() {
    // TODO
    return true;
  }

  get engine() {
    return this.framework.get(WorkspaceEngineService).engine;
  }

  name$ = LiveData.from<string | undefined>(
    yjsGetPath(this.rootYDoc.getMap('meta'), 'name') as Observable<
      string | undefined
    >,
    undefined
  );

  avatar$ = LiveData.from(
    yjsGetPath(this.rootYDoc.getMap('meta'), 'avatar') as Observable<
      string | undefined
    >,
    undefined
  );

  setAvatar(avatar: string) {
    transact(
      this.rootYDoc,
      () => {
        this.rootYDoc.getMap('meta').set('avatar', avatar);
      },
      { force: true }
    );
  }

  setName(name: string) {
    transact(
      this.rootYDoc,
      () => {
        this.rootYDoc.getMap('meta').set('name', name);
      },
      { force: true }
    );
  }

  override dispose(): void {
    this.docCollection.dispose();
  }
}
